"use client";

import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowRight, ArrowLeft, FileText, Network, Users, X, Sparkles } from "lucide-react";
import { cn } from "@/lib/utils";

const STORAGE_KEY = "wiki_arsenal_welcome_seen";

const SLIDES = [
  {
    icon: Sparkles,
    title: "Bienvenido a Arsenal Wiki",
    description: "Una plataforma de conocimiento colaborativo con documentos, grafos de conexiones y tareas en tiempo real.",
  },
  {
    icon: FileText,
    title: "Documentos colaborativos",
    description: "Edita notas en Markdown con tu equipo. Usa [[Título]] para crear vínculos entre documentos automáticamente.",
  },
  {
    icon: Network,
    title: "Visualiza tu conocimiento",
    description: "Cada nota es un nodo en una red. Descubre patrones y conexiones que no verías en una lista.",
  },
  {
    icon: Users,
    title: "Trabaja en equipo",
    description: "Invita miembros, asigna tareas y comparte el conocimiento de tu organización de forma segura.",
  },
];

/**
 * Modal de bienvenida que se muestra la primera vez que el usuario entra.
 * 3 slides introductorias con CTA "Empezar".
 *
 * Persiste en localStorage para no volver a aparecer.
 */
export function WelcomeModal() {
  const [open, setOpen] = useState(false);
  const [index, setIndex] = useState(0);

  useEffect(() => {
    if (typeof window === "undefined") return;
    const seen = localStorage.getItem(STORAGE_KEY) === "1";
    if (!seen) {
      // Pequeño delay para que la app termine de hidratar
      const t = setTimeout(() => setOpen(true), 600);
      return () => clearTimeout(t);
    }
  }, []);

  const handleClose = () => {
    localStorage.setItem(STORAGE_KEY, "1");
    setOpen(false);
  };

  const next = () => {
    if (index < SLIDES.length - 1) {
      setIndex((i) => i + 1);
    } else {
      handleClose();
    }
  };

  const prev = () => setIndex((i) => Math.max(0, i - 1));

  const current = SLIDES[index];
  const Icon = current.icon;
  const isLast = index === SLIDES.length - 1;

  if (!open) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-[500] flex items-center justify-center p-4 bg-background/80 backdrop-blur-md"
        role="dialog"
        aria-modal="true"
        aria-labelledby="welcome-title"
      >
        <motion.div
          initial={{ opacity: 0, scale: 0.95, y: 8 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.95 }}
          transition={{ duration: 0.2, ease: [0.16, 1, 0.3, 1] }}
          className="relative w-full max-w-md rounded-2xl border border-border/60 bg-card p-8 shadow-2xl"
        >
          <button
            onClick={handleClose}
            className="absolute top-3 right-3 p-1 rounded-md text-muted-foreground/60 hover:text-foreground hover:bg-secondary/60 transition-colors"
            aria-label="Cerrar bienvenida"
          >
            <X className="w-4 h-4" />
          </button>

          <AnimatePresence mode="wait">
            <motion.div
              key={index}
              initial={{ opacity: 0, x: 12 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -12 }}
              transition={{ duration: 0.18 }}
              className="flex flex-col items-center text-center"
            >
              <div className="w-14 h-14 rounded-2xl bg-primary/10 border border-primary/20 flex items-center justify-center text-primary mb-5">
                <Icon className="w-7 h-7" strokeWidth={1.5} />
              </div>
              <h2
                id="welcome-title"
                className="text-lg font-bold text-foreground mb-2 text-balance"
              >
                {current.title}
              </h2>
              <p className="text-sm text-muted-foreground max-w-sm leading-relaxed text-pretty">
                {current.description}
              </p>
            </motion.div>
          </AnimatePresence>

          <div className="flex items-center justify-center gap-1.5 mt-6 mb-4">
            {SLIDES.map((_, i) => (
              <span
                key={i}
                className={cn(
                  "h-1 rounded-full transition-all",
                  i === index ? "w-6 bg-primary" : "w-1.5 bg-secondary",
                )}
              />
            ))}
          </div>

          <div className="flex items-center justify-between">
            <button
              onClick={prev}
              disabled={index === 0}
              className="flex items-center gap-1 px-3 py-1.5 rounded-md text-xs font-medium text-muted-foreground hover:text-foreground hover:bg-secondary/60 transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
            >
              <ArrowLeft className="w-3 h-3" />
              Atrás
            </button>
            <button
              onClick={next}
              className="flex items-center gap-1.5 px-4 py-2 bg-primary text-primary-foreground rounded-lg text-xs font-medium hover:bg-primary/90 active:scale-[0.98] transition-all shadow-sm shadow-primary/15"
            >
              {isLast ? "Empezar" : "Siguiente"}
              <ArrowRight className="w-3.5 h-3.5" />
            </button>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}
